import {
  type LeagueCardData,
  disciplineLabel,
  disciplineVariant,
  venueLine,
} from '../leagues/league-card';
import { cleanTournamentName } from '../utils/cleanTournamentName';

export interface TournamentItem {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  venue: string;
  status: string;
}

export type DisciplineFilter = 'ALL' | 'singles' | 'doubles' | 'mixed';
export type KindFilter = 'ALL' | 'tournament' | 'league';

export interface EntryLink {
  label: string;
  to: string;
}

export interface FeedEntry {
  key: string;
  kind: 'tournament' | 'league';
  startDate: Date | null;
  endDate: Date | null;
  title: EntryLink;
  round: EntryLink | null;
  organizer: EntryLink | null;
  venue: string | null;
  discipline: { label: string; variant: 'singles' | 'doubles' | 'mixed' } | null;
  signupOpen: boolean;
  cta: EntryLink;
  searchText: string;
}

export interface Feed {
  upcoming: FeedEntry[];
  ongoing: FeedEntry[];
  past: FeedEntry[];
}

export interface MonthGroup {
  key: string;
  label: string;
  entries: FeedEntry[];
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function toDateOnly(iso: string): Date {
  return startOfDay(new Date(iso));
}

export function isSignupOpen(league: LeagueCardData): boolean {
  return Boolean(league.signupMode && league.signupMode !== 'PRIVATE');
}

function tournamentEntry(tournament: TournamentItem): FeedEntry {
  const name = cleanTournamentName(tournament.name);
  const to = `/events/${tournament.id}`;

  return {
    key: `t-${tournament.id}`,
    kind: 'tournament',
    startDate: toDateOnly(tournament.startDate),
    endDate: toDateOnly(tournament.endDate),
    title: { label: name, to },
    round: null,
    organizer: null,
    venue: tournament.venue || null,
    discipline: null,
    signupOpen: false,
    cta: { label: 'Szczegóły', to },
    searchText: [name, tournament.venue].filter(Boolean).join(' ').toLowerCase(),
  };
}

function leagueEntry(league: LeagueCardData): FeedEntry {
  const name = cleanTournamentName(league.name);
  const to = `/leagues/${league.id}`;
  const signupOpen = isSignupOpen(league);
  const next = league.nextRound ?? null;
  const last = league.lastRound ?? null;
  const anchor = next ?? last;
  const club = league.club && league.club.slug !== 'global' ? league.club : null;
  const venue = venueLine(league);

  return {
    key: `l-${league.id}`,
    kind: 'league',
    startDate: anchor?.startDate ? toDateOnly(anchor.startDate) : null,
    endDate: null,
    title: { label: name, to },
    round: next ? { label: next.name, to: `${to}/rounds/${next.id}` } : null,
    organizer: club ? { label: club.name, to: `/clubs/${club.slug}` } : null,
    venue,
    discipline: {
      label: disciplineLabel(league.discipline),
      variant: disciplineVariant(league.discipline),
    },
    signupOpen,
    cta: signupOpen
      ? { label: 'Zapisz się', to: `${to}#zapisy` }
      : { label: 'Szczegóły', to },
    searchText: [name, club?.name, league.venue, league.venueCity]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  };
}

function byDateAscThenName(a: FeedEntry, b: FeedEntry): number {
  const aTime = a.startDate ? a.startDate.getTime() : Number.MAX_SAFE_INTEGER;
  const bTime = b.startDate ? b.startDate.getTime() : Number.MAX_SAFE_INTEGER;
  if (aTime !== bTime) {
    return aTime - bTime;
  }

  return a.title.label.localeCompare(b.title.label, 'pl');
}

function bySignupThenDateDesc(a: FeedEntry, b: FeedEntry): number {
  if (a.signupOpen !== b.signupOpen) {
    return a.signupOpen ? -1 : 1;
  }
  const aTime = a.startDate ? a.startDate.getTime() : 0;
  const bTime = b.startDate ? b.startDate.getTime() : 0;
  if (aTime !== bTime) {
    return bTime - aTime;
  }

  return a.title.label.localeCompare(b.title.label, 'pl');
}

// Splits everything the public list can show into three shelves: what is
// scheduled ahead, what runs without a scheduled kolejka, and what is over.
export function buildFeed(
  tournaments: TournamentItem[],
  leagues: LeagueCardData[],
  today: Date
): Feed {
  const todayTime = startOfDay(today).getTime();
  const upcoming: FeedEntry[] = [];
  const ongoing: FeedEntry[] = [];
  const past: FeedEntry[] = [];

  for (const tournament of tournaments) {
    const entry = tournamentEntry(tournament);
    const end = entry.endDate ?? entry.startDate;
    if (end && end.getTime() >= todayTime) {
      upcoming.push(entry);
    } else {
      past.push(entry);
    }
  }

  for (const league of leagues) {
    if (league.status !== 'ACTIVE' && !isSignupOpen(league)) {
      continue;
    }

    const entry = leagueEntry(league);
    if (entry.round && entry.startDate && entry.startDate.getTime() >= todayTime) {
      upcoming.push(entry);
    } else {
      ongoing.push(entry);
    }
  }

  return {
    upcoming: upcoming.sort(byDateAscThenName),
    ongoing: ongoing.sort(bySignupThenDateDesc),
    past: past.sort((a, b) => -byDateAscThenName(a, b)),
  };
}

export function filterEntries(
  entries: FeedEntry[],
  filters: { search: string; discipline: DisciplineFilter; kind: KindFilter }
): FeedEntry[] {
  const query = filters.search.trim().toLowerCase();

  return entries.filter((entry) => {
    if (filters.kind !== 'ALL' && entry.kind !== filters.kind) {
      return false;
    }
    if (filters.discipline !== 'ALL' && entry.discipline?.variant !== filters.discipline) {
      return false;
    }
    if (query && !entry.searchText.includes(query)) {
      return false;
    }

    return true;
  });
}

export function groupByMonth(entries: FeedEntry[]): MonthGroup[] {
  const groups: MonthGroup[] = [];

  for (const entry of entries) {
    const key = entry.startDate
      ? `${entry.startDate.getFullYear()}-${entry.startDate.getMonth()}`
      : 'undated';
    const label = entry.startDate
      ? entry.startDate.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })
      : 'Termin nieustalony';
    const current = groups[groups.length - 1];

    if (current && current.key === key) {
      current.entries.push(entry);
      continue;
    }

    groups.push({ key, label, entries: [entry] });
  }

  return groups;
}
